import { LoggerService } from '@angular-ru/cdk/logger';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { EMPTY, filter, interval, map, of, scan, startWith, switchMap, take, takeWhile, timer } from 'rxjs';
import { CountdownFormat, ICountdown, ICountdownLabels } from 'src/app/api/modules/core/dynamic/components/countdown/ICountdown';
import { ICountdownResponseData } from 'src/app/api/modules/core/dynamic/components/countdown/network/response/ICountdownResponseData';
import { createDigitaServiceError } from 'src/app/app-error';
import { LinkService } from '../../../services/link.service';
import { DigitaServiceAPIService } from '../../../services/network/digitaservice-api.service';
import { CountdownSectionComponent } from './countdown-section/countdown-section.component';
import { createInitialSections, secondsToCountdown } from './countdown.utils';

/**
 * The Countdown Component is used to display a stylized split-flap countdown timer.
 *
 * Times are anchored to the server clock: the config carries the server's
 * `startTime`/`endTime`, and the remaining time is derived from how much
 * client time has passed since the config arrived — so a skewed client clock
 * cannot distort the countdown, and interval drift self-corrects.
 *
 * When the countdown reaches zero and an `onCompleteAPI` is configured, the
 * endpoint is called once and its response (destination + optional delay)
 * drives a navigation.
 *
 * See {@link ICountdown}.
 */
@Component({
  selector: 'app-countdown',
  templateUrl: './countdown.component.html',
  styleUrls: ['./countdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CountdownSectionComponent],
})
export class CountdownComponent {
  private readonly loggerService = inject(LoggerService);
  private readonly apiService = inject(DigitaServiceAPIService);
  private readonly linkService = inject(LinkService);

  /**
   * The configuration powering this component.
   *
   * Nothing renders until a truthy, valid config arrives.
   */
  readonly config = input<ICountdown | null>();

  /**
   * The validated configuration, or a warning when the config can't be used
   * (logged by an effect; the component then renders nothing).
   */
  private readonly viewConfig = computed(() => {
    const configuration = this.config();
    if (!configuration) {
      return undefined;
    }

    const warn = (warning: string) => ({ valid: false as const, warning });

    // format
    const format = configuration.format;
    if (
      format !== CountdownFormat.DAYS &&
      format !== CountdownFormat.HOURS &&
      format !== CountdownFormat.MINUTES &&
      format !== CountdownFormat.SECONDS
    ) {
      return warn(`No "format" provided. This should be "days" "hours" "minutes" or "seconds".`);
    }

    // labels — optional, but all-or-nothing
    let labels: ICountdownLabels | undefined = undefined;
    if (configuration.labels) {
      for (const [key, label] of Object.entries(configuration.labels)) {
        if (typeof label !== 'string' || label.length === 0) {
          return warn(`The "labels.${key}" property must be provided otherwise no labels should be used.`);
        }
      }
      labels = configuration.labels;
    }

    // times
    if (typeof configuration.startTime !== 'number' || configuration.startTime < 0) {
      return warn(`The "startTime" property must be provided and be a number greater than 0.`);
    }
    if (typeof configuration.endTime !== 'number' || configuration.endTime < 0) {
      return warn(`The "endTime" property must be provided and be a number greater than 0.`);
    }

    return {
      valid: true as const,
      warning: undefined,
      format,
      labels,
      startTime: configuration.startTime,
      endTime: configuration.endTime,
      // Note: this can be empty if the countdown is for display purposes only.
      onCompleteAPI: configuration.onCompleteAPI,
    };
  });

  /**
   * The ticking countdown data. A `scan` carries the prev/next digit pairs
   * between ticks (they drive the split-flap animation), and the stream
   * completes once the countdown reaches zero.
   */
  protected readonly countdownData = toSignal(
    toObservable(this.viewConfig).pipe(
      switchMap((view) => {
        if (!view?.valid) {
          return of(undefined);
        }

        // when the client received the config; remaining time is computed
        // against this anchor on every tick
        const initTime = Math.floor(Date.now() / 1000);
        const initial = secondsToCountdown(view.startTime, view.endTime, initTime, view.format, createInitialSections(), view.labels, true);

        return interval(1000).pipe(
          scan((data) => secondsToCountdown(view.startTime, view.endTime, initTime, view.format, data, view.labels, false), initial),
          startWith(initial),
          takeWhile((data) => data.remainingSeconds > 0, true),
        );
      }),
    ),
    { initialValue: undefined },
  );

  constructor() {
    // surface configuration problems (once per config change)
    effect(() => {
      const warning = this.viewConfig()?.warning;
      if (warning) {
        this.loggerService.warn(`[Countdown] - ${warning}`);
      }
    });

    // when the countdown completes and an API is configured: call it once,
    // wait the configured delay, then navigate to the destination
    toObservable(
      computed(() => {
        const view = this.viewConfig();
        const data = this.countdownData();
        return view?.valid && view.onCompleteAPI && data?.remainingSeconds === 0 ? view.onCompleteAPI : undefined;
      }),
    )
      .pipe(
        filter((onCompleteAPI): onCompleteAPI is string => !!onCompleteAPI),
        take(1),
        switchMap((onCompleteAPI) => this.apiService.data<ICountdownResponseData>(onCompleteAPI)),
        switchMap((response) => {
          // no response → nothing to do
          if (!response) {
            return EMPTY;
          }
          // a completion response without a destination would strand the user
          // on a dead screen — fail loud so the error surfaces
          if (!response.destination) {
            throw createDigitaServiceError(
              'Countdown',
              'response',
              `The "destination" property must be provided as part of the Countdown API Response.`,
              'config',
            );
          }
          return timer(response.destinationDelay || 0).pipe(map(() => response.destination));
        }),
        takeUntilDestroyed(),
      )
      .subscribe((destination) => {
        this.linkService.openLink(destination);
      });
  }
}
